import baseUrl from "../../api/baseUrl";
import companyApiClient from "../../api/companyApiClient";
import { getDecryptedToken } from "../../utils/crypto/cryptoUtils";

export interface Plan {
  id: number;
  name: string;
  billingType: "daily" | "weekly" | "monthly" | "yearly" | "custom";
  amount: number;
  currency: string;
  customDays?: number;
}

export interface CompanySubscription {
  id: number;
  companyId: number;
  planId: number;
  startDate: string;
  endDate: string;
  status: "pending" | "active" | "expired" | "cancelled";
  autoRenew: boolean;
  payHereSubscriptionId?: string;
  lastPaymentDate?: string;
  paidUntilDate?: string;
  daysSinceLastPayment?: number;
  isPaidPeriod?: boolean;
  daysUntilDue?: number;
  Plan: Plan;
}

export interface SubscriptionData {
  subscription: CompanySubscription | null;
  timeRemaining: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    expired: boolean;
  };
  isActive: boolean;
}

const subscriptionService = {
  // Get current company's subscription using company token (for company dashboard)
  getCompanySubscriptionWithCompanyToken:
    async (): Promise<SubscriptionData> => {
      try {
        const companyId = getDecryptedToken("companyId");
        console.log(
          "Fetching subscription for company (company token):",
          companyId,
        );

        if (!companyId) {
          throw new Error("Company ID not found");
        }

        const response = await companyApiClient.get(
          `/company/subscriptions/company/${companyId}`,
        );

        const subscription: CompanySubscription | null =
          response.data.data.subscription;

        if (!subscription) {
          return {
            subscription: null,
            timeRemaining: {
              days: 0,
              hours: 0,
              minutes: 0,
              seconds: 0,
              expired: true,
            },
            isActive: false,
          };
        }

        // Calculate time remaining
        const endDate = new Date(subscription.endDate);
        const now = new Date();
        const timeRemaining = subscriptionService.calculateTimeRemaining(
          endDate,
          now,
        );

        return {
          subscription,
          timeRemaining,
          isActive: subscription.status === "active",
        };
      } catch (error) {
        console.error("Error fetching subscription:", error);
        throw error;
      }
    },

  // Get current company's subscription from token (for branch context)
  getCurrentSubscription: async (): Promise<SubscriptionData> => {
    try {
      const companyId = getDecryptedToken("companyId");
      console.log("Fetching subscription for company:", companyId);

      if (!companyId) {
        throw new Error("Company ID not found");
      }

      const response = await baseUrl.get(
        `/branch/subscription/company/${companyId}`,
      );

      const subscription: CompanySubscription | null =
        response.data.data.subscription;

      if (!subscription) {
        return {
          subscription: null,
          timeRemaining: {
            days: 0,
            hours: 0,
            minutes: 0,
            seconds: 0,
            expired: true,
          },
          isActive: false,
        };
      }

      // Calculate time remaining
      const endDate = new Date(subscription.endDate);
      const now = new Date();
      const timeRemaining = subscriptionService.calculateTimeRemaining(
        endDate,
        now,
      );

      return {
        subscription,
        timeRemaining,
        isActive: subscription.status === "active",
      };
    } catch (error) {
      console.error("Error fetching subscription:", error);
      throw error;
    }
  },

  // Get current branch's subscription from branch token (for POS)
  getCurrentBranchSubscription: async (): Promise<SubscriptionData> => {
    try {
      const response = await baseUrl.get(`/branch/subscription/current`);

      const subscription: CompanySubscription | null =
        response.data.data.subscription;

      if (!subscription) {
        return {
          subscription: null,
          timeRemaining: {
            days: 0,
            hours: 0,
            minutes: 0,
            seconds: 0,
            expired: true,
          },
          isActive: false,
        };
      }

      // Calculate time remaining
      const endDate = new Date(subscription.endDate);
      const now = new Date();
      const timeRemaining = subscriptionService.calculateTimeRemaining(
        endDate,
        now,
      );

      return {
        subscription,
        timeRemaining,
        isActive: subscription.status === "active",
      };
    } catch (error) {
      console.error("Error fetching branch subscription:", error);
      throw error;
    }
  },

  // Get subscription history for a company
  getSubscriptionHistory: async (
    companyId: string,
  ): Promise<CompanySubscription[]> => {
    try {
      const response = await baseUrl.get(
        `/branch/subscription/history/${companyId}`,
      );
      return response.data.data.subscriptions || [];
    } catch (error) {
      console.error("Error fetching subscription history:", error);
      throw error;
    }
  },

  // Calculate time remaining until end date
  calculateTimeRemaining: (
    endDate: Date,
    currentDate: Date = new Date(),
  ): {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    expired: boolean;
  } => {
    const diff = endDate.getTime() - currentDate.getTime();

    if (diff <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds, expired: false };
  },

  // Format time for display
  formatTimeRemaining: (timeRemaining: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  }): string => {
    const { days, hours, minutes, seconds } = timeRemaining;
    return `${days}d ${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  },
};

export default subscriptionService;
